export type ItemCategory = {
  id: number
  name: string
}

export type ViewItem = ItemCategory & {
  isDeleted: boolean
}

export type ItemCategoriesState = {
  addedItemCategories: ItemCategory[]
  updatedItemCategories: ItemCategory[]
  deletedItemCategoryIds: number[]
}

export const defaultItemCategory = (): ItemCategory => ({
  id: 0,
  name: '',
})

export const defaultItemCategoriesState = (): ItemCategoriesState => ({
  addedItemCategories: [],
  updatedItemCategories: [],
  deletedItemCategoryIds: []
})

export const hasChanges = (state: ItemCategoriesState): boolean => {
  return state.addedItemCategories.length > 0 ||
    state.updatedItemCategories.length > 0 ||
    state.deletedItemCategoryIds.length > 0
}

export const updateItemCategory = (state: ItemCategoriesState, serverItemCategories: ItemCategory[], itemCategoryId: number, update: (item: ItemCategory) => ItemCategory): ItemCategoriesState => {
  if (state.addedItemCategories.some(x => x.id === itemCategoryId)) {
    return {
      ...state,
      addedItemCategories: state.addedItemCategories.map(x => x.id === itemCategoryId ? update(x) : x)
    }
  }

  if (state.updatedItemCategories.some(x => x.id === itemCategoryId)) {
    return {
      ...state,
      updatedItemCategories: state.updatedItemCategories.map(x => x.id === itemCategoryId ? update(x) : x)
    }
  }

  const serverItem = serverItemCategories.find(x => x.id === itemCategoryId)
  if (serverItem) {
    return {
      ...state,
      updatedItemCategories: [
        ...state.updatedItemCategories,
        update(serverItem)
      ]
    }
  }

  return state
}

export const addItemCategory = (state: ItemCategoriesState, name: string | null | undefined): ItemCategoriesState => {
  if (!name) {
    return state
  }
  return {
    ...state,
    addedItemCategories: [...state.addedItemCategories, { ...defaultItemCategory(), name, id: -state.addedItemCategories.length }]
  }
}

const getUpdatedItem = (state: ItemCategoriesState, serverItem: ItemCategory): ItemCategory => {
  const updated = state.updatedItemCategories.find(x => x.id === serverItem.id)
  return updated ?? serverItem
}

const getAllUpdatedItems = (state: ItemCategoriesState, serverItems: ItemCategory[]) => {
  return serverItems.map(x => getUpdatedItem(state, x)).concat(state.addedItemCategories)
}

export const createViewItems = (state: ItemCategoriesState, serverItems: ItemCategory[]): ViewItem[] => {
  const rows = getAllUpdatedItems(state, serverItems).map(x => {
    const isDeleted = state.deletedItemCategoryIds.includes(x.id)
    return {
      ...x,
      isDeleted
    }
  })

  return rows.sort((a, b) => a.name.localeCompare(b.name))
}

export const deleteItemCategory = (state: ItemCategoriesState, itemCategoryId: number): ItemCategoriesState => {
  if (state.deletedItemCategoryIds.some(x => x === itemCategoryId)) {
    return state
  }
  return {
    ...state,
    deletedItemCategoryIds: [...state.deletedItemCategoryIds, itemCategoryId]
  }
}

export const undeleteItemCategory = (state: ItemCategoriesState, itemCategoryId: number): ItemCategoriesState => {
  return { ...state, deletedItemCategoryIds: state.deletedItemCategoryIds.filter(x => x !== itemCategoryId) }
}

export const buildStateForSave = (state: ItemCategoriesState): ItemCategoriesState => {
  return {
    ...state,
    addedItemCategories: state.addedItemCategories.filter(x => !state.deletedItemCategoryIds.includes(x.id)),
    updatedItemCategories: state.updatedItemCategories.filter(x => !state.deletedItemCategoryIds.includes(x.id)),
  }
}

export const onSave = (state: ItemCategoriesState): ItemCategoriesState => {
  return buildStateForSave(state)
}
